import React, {
  useCallback,
  useRef,
  useEffect,
  ReactNode,
  useState,
} from "react"
import { Dropzone } from "./Dropzone"
import { FileUploadChild } from "./FileUploadChild"
import { ImageCropPanel } from "./ImageCropPanel"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import { populateCmsTemplate } from "../../utils"
import { PhotoValidationOptions, validateFile } from "../../file"
import ExpandableCard from "../ExpandableCard"
import Button from "../Button"
import ColumnContainer from "../Layout/ColumnContainer"
import cn from "../../../../utils/classes"
import { SemanticHeader } from "../SemanticHeader"

export interface FileUploadType {
  name: string
  isUploading?: boolean
}

export interface ImageCropConfig {
  onCropSet: (croppedFile: File) => void
  isProcessing?: boolean
}

export interface FileUploadProps {
  title: string
  tableTitle?: string
  description?: ReactNode
  isRequired?: boolean
  hideDeleteWhenDisabled?: boolean
  disabled?: boolean
  defaultOpen?: boolean
  showAccordion?: boolean
  showUpload?: boolean
  selectedFile?: FileUploadType
  onFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    errorMessage?: string,
  ) => void
  onRemoveClick: () => void
  onUploadClick: () => void
  dropzoneErrorMessage?: string
  fileUploadErrorMessage?: string
  tableComponent?: React.ReactNode
  uploadSuccessMessage?: string // Added prop for upload success message
  isImageUploadOnly?: boolean
  tooltipText?: string
  inputChildren?: ReactNode
  useUploadLabel?: boolean // Flag to conditionally show upload label
  useExpandableCard?: boolean // Flag to conditionally wrap in ExpandableCard
  /** Remove useAccordion once https://dev.azure.com/HACloud/VEAP/_workitems/edit/17252 is in prod: */
  useAccordion?: boolean // Flag to conditionally wrap in Accordion
  shouldShowUploadButton?: boolean // Flag to conditionally show upload button
  containerStyleOverrides?: string // Optional style overrides for the container
  uploadContentStyleOverrides?: string // Optional style overrides for upload content container
  cardStyleOverride?: string // Optional style overrides for the card
  imageCropConfig?: ImageCropConfig
}

export const FileUpload: React.FC<FileUploadProps> = ({
  title,
  tableTitle = "",
  description,
  isRequired,
  disabled = false,
  selectedFile = undefined,
  defaultOpen = true,
  showAccordion = true,
  showUpload = true,
  onFileChange,
  onRemoveClick,
  onUploadClick,
  dropzoneErrorMessage,
  fileUploadErrorMessage,
  tableComponent = undefined,
  uploadSuccessMessage = "", // Default to empty string
  isImageUploadOnly = false,
  tooltipText,
  inputChildren,
  useUploadLabel = true,
  useExpandableCard = true,
  useAccordion = true,
  shouldShowUploadButton = true,
  containerStyleOverrides = "",
  uploadContentStyleOverrides = "",
  cardStyleOverride = "",
  imageCropConfig,
}) => {
  const { cms: cmsContent } = useCMSReady(["common"])
  const common = cmsContent.common

  const uploadButtonWrapperRef = useRef<HTMLDivElement>(null)
  const removeButtonRef = useRef<HTMLButtonElement>(null)
  const [dropzoneErrorMessageOverride, setDropzoneErrorMessageOverride] =
    useState<string>("")
  const [hasAppliedInitialForceOpen, setHasAppliedInitialForceOpen] =
    useState(false)
  const [stagedFileData, setStagedFileData] = useState<File | undefined>()
  const [isCropReady, setIsCropReady] = useState(false)

  // Track whether we should apply forceOpen (only on first time it's provided)
  useEffect(() => {
    if (defaultOpen && !hasAppliedInitialForceOpen) {
      setHasAppliedInitialForceOpen(true)
    }
  }, [defaultOpen, hasAppliedInitialForceOpen])

  // useEffect to focus the upload button when a file is selected and not uploading or in error state
  useEffect(() => {
    if (
      selectedFile &&
      !selectedFile.isUploading &&
      !fileUploadErrorMessage &&
      uploadButtonWrapperRef.current
    ) {
      uploadButtonWrapperRef.current.focus()
    }
  }, [selectedFile, fileUploadErrorMessage])

  // useEffect to focus the remove button when an error occurs
  useEffect(() => {
    if (fileUploadErrorMessage && removeButtonRef.current) {
      removeButtonRef.current.focus()
    }
  }, [fileUploadErrorMessage])

  const handleRemoveClick = useCallback(() => {
    setStagedFileData(undefined)
    onRemoveClick()
  }, [onRemoveClick])

  const handleOnFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setStagedFileData(undefined)
    onRemoveClick() // Clear any existing file or error

    // Validate file type and file size
    const file = event.target.files?.[0]
    if (!file) {
      setDropzoneErrorMessageOverride(common?.uploadFailed ?? "")
      return
    }

    // Check for file validation errors
    const validationExclusions = isImageUploadOnly
      ? [PhotoValidationOptions.NON_IMAGE_TYPE]
      : []

    const { errorMessage } = await validateFile(
      file,
      {
        fileUploadError: common?.uploadFailed ?? "",
        errorFileType: common?.uploadInvalidFileType ?? "",
        errorFileSize: common?.uploadFileTooLarge ?? "",
        errorImageTooSmall: common?.imageTooSmallError ?? "",
        errorImageInvalidAspectRatio:
          common?.imageInvalidAspectRatioError ?? "",
      },
      validationExclusions,
    )

    setDropzoneErrorMessageOverride("")

    if (!errorMessage) {
      setStagedFileData(file)
    }

    // If valid, call onFileChange
    onFileChange(event, errorMessage)
  }

  const handleCropSet = (croppedFile: File) => {
    imageCropConfig!.onCropSet(croppedFile)
  }

  const showCropPanel =
    !!imageCropConfig &&
    !!stagedFileData &&
    !!selectedFile &&
    !selectedFile.isUploading &&
    !fileUploadErrorMessage

  // Extract the upload content to reuse with or without Accordion wrapper
  const uploadContent = (
    <div
      className={cn(
        "flex flex-col space-y-6 w-[360px] px-8 py-8 border-[2px] border-solid border-gray-300 rounded-lg",
        uploadContentStyleOverrides,
      )}
    >
      {inputChildren}
      {showUpload ? (
        <>
          <Dropzone
            label={useUploadLabel ? (common?.uploadFile ?? "") : ""}
            isRequired={isRequired}
            isDisabled={disabled}
            onFileChange={handleOnFileChange}
            isLoading={selectedFile?.isUploading || false}
            errorMessage={dropzoneErrorMessageOverride || dropzoneErrorMessage}
            isImageUploadOnly={isImageUploadOnly}
          />
          {!!selectedFile && (
            <FileUploadChild
              label={common?.selectedFile ?? ""}
              name={selectedFile?.name || ""}
              onRemoveClick={handleRemoveClick}
              isLoading={selectedFile?.isUploading || false}
              error={fileUploadErrorMessage}
              removeButtonRef={removeButtonRef}
            />
          )}
          {showCropPanel && (
            <ImageCropPanel
              file={stagedFileData!}
              onCropSet={handleCropSet}
              onCropReadyChange={setIsCropReady}
              isProcessing={imageCropConfig!.isProcessing}
            />
          )}
          {!!selectedFile &&
            !selectedFile?.isUploading &&
            !fileUploadErrorMessage &&
            shouldShowUploadButton &&
            (!imageCropConfig || isCropReady) && (
              <div ref={uploadButtonWrapperRef} className="!mt-0">
                <Button
                  onClick={onUploadClick}
                  buttonLabel={common?.uploadFile ?? ""}
                  rootStyleOverrides="w-full !mt-3"
                  styleOverrides="rounded-lg"
                />
              </div>
            )}
        </>
      ) : undefined}
      {/* Persistent live region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {fileUploadErrorMessage
          ? fileUploadErrorMessage
          : selectedFile?.isUploading
            ? populateCmsTemplate(
                [selectedFile.name],
                "cms.common.uploadingTemplate",
                common?.uploadingTemplate,
              )
            : uploadSuccessMessage
              ? uploadSuccessMessage
              : ""}
      </div>
    </div>
  )

  const CardComponent = ExpandableCard

  const shouldShowAccordion =
    showAccordion && (useExpandableCard || useAccordion)
  return (
    <div
      className={cn(
        "flex flex-col space-y-6 justify-center items-left",
        "w-full",
        containerStyleOverrides,
      )}
    >
      {shouldShowAccordion ? (
        <CardComponent
          key={
            hasAppliedInitialForceOpen
              ? "expandable-card-controlled"
              : "expandable-card-default"
          }
          title={title}
          descriptionComponents={description}
          defaultOpen={defaultOpen}
          tooltipText={tooltipText}
          cardStyleOverride={cardStyleOverride}
          labelStyleOverides="text-gray-700"
        >
          {uploadContent}
        </CardComponent>
      ) : (
        uploadContent
      )}
      {!!tableComponent && (
        <ColumnContainer styleOverrides="space-y-2">
          <SemanticHeader
            headerLevel="h3"
            text={tableTitle}
            type="thirdLevelHeader"
            styleOverrides="m-0"
          />
          {tableComponent}
        </ColumnContainer>
      )}
    </div>
  )
}
